/**
 * Check In workflow — return an asset to inventory.
 * 1. Search/scan to find the asset
 * 2. Confirm (with optional status + notes) and queue check-in
 */

import { router } from '../router.js';
import { enqueue } from '../db.js';
import { syncDirect } from '../sync.js';
import { searchAssets, getAssetByTag, getStatusLabels } from '../snipeit.js';
import { showToast } from '../toast.js';
import { hapticSuccess, hapticTap } from '../haptics.js';
import { startScanner, stopScanner } from './scanner.js';

export function renderCheckin() {
  const app = document.getElementById('app');

  app.innerHTML = `
    <div class="page">
      <!-- Screen-reader live region for search result announcements -->
      <div id="ci-sr-live" aria-live="polite" aria-atomic="true" style="position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;"></div>

      <div class="page-header">
        <button class="back-btn" id="back-btn" aria-label="Back">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <h1>Check In</h1>
      </div>

      <div class="card">
        <h2 style="margin-bottom:12px;">Find Asset to Check In</h2>

        <div style="display:flex; gap:8px; margin-bottom:12px;">
          <button class="btn btn-secondary btn-sm" id="ci-scan-btn">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="7" y1="12" x2="17" y2="12"/></svg>
            Scan
          </button>
        </div>

        <div id="ci-scanner-area" class="hidden">
          <div class="camera-container" id="ci-scanner-reader"></div>
          <button class="btn btn-secondary btn-block btn-sm" id="ci-scanner-close">Close Scanner</button>
        </div>

        <div class="search-wrap">
          <span class="search-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          </span>
          <input type="text" id="ci-asset-search" class="form-control" placeholder="Search by name, tag, or serial..." style="padding-left:38px;" aria-autocomplete="list" aria-controls="ci-asset-results" />
        </div>

        <div id="ci-asset-results" class="search-results hidden" role="listbox"></div>
      </div>

      <!-- Selected asset detail -->
      <div id="ci-selected" class="hidden">
        <div class="card" style="margin-top:12px;">
          <h2 id="ci-asset-name" style="margin-bottom:4px;"></h2>
          <div style="font-size:0.85rem; color:var(--color-text-muted); margin-bottom:8px;" id="ci-asset-detail"></div>
          <div id="ci-assigned-to" style="margin-bottom:12px;"></div>

          <div class="form-group">
            <label for="ci-status">Status after Check In</label>
            <select id="ci-status" class="form-control">
              <option value="">Keep current status...</option>
            </select>
          </div>

          <div class="form-group">
            <label for="ci-notes">Check-in Notes (optional)</label>
            <textarea id="ci-notes" class="form-control" rows="2" placeholder="Condition, reason..."></textarea>
          </div>

          <div style="display:flex; gap:8px;">
            <button class="btn btn-secondary" style="flex:1" id="ci-queue">Add to Queue</button>
            <button class="btn btn-primary" style="flex:1" id="ci-sync">Sync Now</button>
          </div>
        </div>
      </div>
    </div>
  `;

  let selectedAssetId = null;
  let searchTimeout = null;

  // ── Load statuses ──
  loadStatuses();

  // ── Back ──
  document.getElementById('back-btn').addEventListener('click', () => {
    router.navigate('/');
  });

  // ── Scan ──
  document.getElementById('ci-scan-btn').addEventListener('click', () => {
    hapticTap();
    const area = document.getElementById('ci-scanner-area');
    area.classList.remove('hidden');
    startScanner('ci-scanner-reader', async (text) => {
      hapticSuccess();
      stopScanner();
      area.classList.add('hidden');
      document.getElementById('ci-asset-search').value = text;
      try {
        const asset = await getAssetByTag(text);
        selectAsset(asset);
      } catch {
        showToast('Asset not found for tag: ' + text);
      }
    });
  });

  document.getElementById('ci-scanner-close').addEventListener('click', () => {
    stopScanner();
    document.getElementById('ci-scanner-area').classList.add('hidden');
  });

  // ── Asset search ──
  document.getElementById('ci-asset-search').addEventListener('input', (e) => {
    clearTimeout(searchTimeout);
    const query = e.target.value.trim();
    if (query.length < 2) {
      document.getElementById('ci-asset-results').classList.add('hidden');
      return;
    }
    searchTimeout = setTimeout(async () => {
      try {
        const res = await searchAssets(query);
        const container = document.getElementById('ci-asset-results');
        const rows = res.rows || [];
        container.innerHTML = '';
        if (rows.length === 0) {
          container.innerHTML = `<div class="result-empty">No results for "${escapeHtml(query)}"</div>`;
          container.classList.remove('hidden');
          document.getElementById('ci-sr-live').textContent = 'No results found';
        } else {
          rows.forEach((a) => {
            const div = document.createElement('div');
            div.className = 'result-item';
            div.setAttribute('role', 'option');
            div.setAttribute('tabindex', '0');
            div.innerHTML = `
              <div>${escapeHtml(a.name || 'Unnamed')}</div>
              <div class="result-sub">Tag: ${escapeHtml(a.asset_tag || '')} | Assigned: ${a.assigned_to ? escapeHtml(a.assigned_to.name || 'Someone') : 'Available'}</div>
            `;
            div.addEventListener('click', () => selectAsset(a));
            div.addEventListener('keydown', (ev) => {
              if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); selectAsset(a); }
            });
            container.appendChild(div);
          });
          container.classList.remove('hidden');
          document.getElementById('ci-sr-live').textContent = `${rows.length} result${rows.length !== 1 ? 's' : ''} found`;
        }
      } catch {
        showToast('Search failed');
      }
    }, 300);
  });

  function selectAsset(asset) {
    selectedAssetId = asset.id;

    document.getElementById('ci-asset-name').textContent = asset.name || 'Unnamed';
    document.getElementById('ci-asset-detail').textContent =
      `Tag: ${asset.asset_tag || 'N/A'} | Serial: ${asset.serial || 'N/A'}`;

    const assignedEl = document.getElementById('ci-assigned-to');
    if (asset.assigned_to) {
      assignedEl.innerHTML = `<span style="font-size:0.9rem;">Currently assigned to: <strong>${escapeHtml(asset.assigned_to.name || 'Unknown')}</strong></span>`;
    } else {
      assignedEl.innerHTML = `<span style="font-size:0.9rem; color:var(--color-text-muted);">Not currently assigned</span>`;
    }

    document.getElementById('ci-selected').classList.remove('hidden');
    document.getElementById('ci-asset-results').classList.add('hidden');
  }

  // ── Submit ──
  function buildCheckinPayload() {
    const notes = document.getElementById('ci-notes').value.trim();
    const statusId = document.getElementById('ci-status').value;
    const payload = {
      asset_id: selectedAssetId,
      // Display name for queue summary — not sent to Snipe-IT API
      asset_name: document.getElementById('ci-asset-name').textContent,
    };
    if (notes) payload.note = notes;
    if (statusId) payload.status_id = parseInt(statusId, 10);
    return payload;
  }

  document.getElementById('ci-queue').addEventListener('click', async () => {
    if (!selectedAssetId) return;
    await enqueue('checkin', buildCheckinPayload());
    hapticSuccess();
    showToast('Check in added to queue');
    window.dispatchEvent(new CustomEvent('queue-updated'));
    router.navigate('/');
  });

  document.getElementById('ci-sync').addEventListener('click', async () => {
    if (!selectedAssetId) return;
    const btn = document.getElementById('ci-sync');
    btn.disabled = true;
    btn.textContent = 'Syncing...';
    try {
      await syncDirect('checkin', buildCheckinPayload());
      hapticSuccess();
      showToast('Checked in');
      router.navigate('/');
    } catch (err) {
      showToast('Sync failed: ' + err.message);
      btn.disabled = false;
      btn.textContent = 'Sync Now';
    }
  });

  return () => {
    stopScanner();
  };
}

async function loadStatuses() {
  const select = document.getElementById('ci-status');
  if (!select) return;
  // Insert a loading placeholder as the first option
  const loadingOpt = document.createElement('option');
  loadingOpt.disabled = true;
  loadingOpt.textContent = 'Loading...';
  select.insertBefore(loadingOpt, select.firstChild);
  try {
    const res = await getStatusLabels();
    loadingOpt.remove();
    (res.rows || []).forEach((s) => {
      const opt = document.createElement('option');
      opt.value = s.id;
      opt.textContent = s.name;
      select.appendChild(opt);
    });
  } catch {
    loadingOpt.textContent = 'Failed to load';
  }
}

function escapeHtml(str) {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}
